import { Driver, DriverHook } from "driver.js";
import { defaultTourConfig } from ".";

// Export the tour steps so they can be reused in GrandTour
export const affiliatesPayoutsTourSteps = [
  {
    element: "#payouts",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        
          <div class="flex -space-x-2">
            <div class="w-6 h-6 bg-brand-200 rounded-full border-2 border-white flex items-center justify-center">
              <svg class="w-3 h-3 text-brand-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
            </div>
            <div class="w-6 h-6 bg-brand-300 rounded-full border-2 border-white flex items-center justify-center">
              <svg class="w-3 h-3 text-brand-700" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 17l6-6 4 4 8-8v3h2V4h-6v2h3l-7 7-4-4-6 6v2z"></path>
              </svg>
            </div>
            </div>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-base font-semibold text-foreground">Welcome to Payout Management</span>
        </div>
      </div>`,
      description: `<div class="space-y-3">
        <p class="text-[12px] text-muted-foreground leading-relaxed">Let's explore your payout dashboard! This tour will guide you through managing your earnings, requesting payouts, and tracking payment status.</p>
        <div class="bg-brand-50 p-3 rounded-lg border-l-4 border-brand-400">
          <div class="flex items-start gap-2">
            <svg class="w-4 h-4 text-brand-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-xs text-brand-800 font-medium">Complete all sections to start managing your payouts successfully!</p>
          </div>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Estimated time: 2 minutes</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#payouts",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        <div class="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
          <svg class="w-6 h-6 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"></path>
          </svg>
        </div>
        <div class="flex items-center gap-2">
        </div>
        </div>
        <span class="text-base font-semibold text-foreground">Current Eligible Balance & Payout Request</span>
      `,
      description: `<div class="space-y-3">
        <p class="text-[12px] text-muted-foreground leading-relaxed">View your current eligible balance and request payouts when ready. The request button is only enabled when you meet the minimum threshold.</p>
        <div class="bg-brand-50 p-3 rounded-lg border-l-4 border-brand-400">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
            </svg>
            <p class="text-xs font-medium text-brand-800">Balance & Payout Features:</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-2">
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span>Real-time balance updates</span>
            </li>
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span>Minimum $10 USD required for payout</span>
            </li>
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span>Instant payout processing</span>
            </li>
          </ul>
        </div>
        <div class="bg-brand-50 p-3 rounded-lg">
          <div class="flex items-center gap-2">
            <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-xs text-brand-800">Your balance updates automatically as you earn commissions</p>
          </div>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#payout-history",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
        <div class="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
          <svg class="w-6 h-6 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
          </svg>
        </div>
        <span class="text-base font-semibold text-foreground">Payout History & Payment Methods</span>
      </div>`,
      description: `<div class="space-y-3">
        <p class="text-[12px] text-muted-foreground leading-relaxed">Track all your past payout requests and their status. Monitor payment progress and view detailed transaction information.</p>
        <div class="bg-brand-50 p-3 rounded-lg border-l-4 border-brand-400">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-xs font-medium text-brand-800">Payout Status Types:</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-2">
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span><strong>Pending:</strong> Request submitted, processing</span>
            </li>
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span><strong>Paid:</strong> Payment completed successfully</span>
            </li>
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span><strong>Rejected:</strong> Payment failed or cancelled</span>
            </li>
          </ul>
        </div>
        <div class="bg-brand-50 p-3 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path>
            </svg>
            <p class="text-xs font-medium text-brand-800">Supported Payment Methods:</p>
          </div>
          <ul class="text-xs text-brand-700 space-y-2">
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span><strong>PayPal:</strong> Instant transfer</span>
            </li>
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span><strong>Venmo:</strong> Instant transfer</span>
            </li>
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span><strong>Zelle:</strong> Instant transfer</span>
            </li>
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span><strong>Bitcoin:</strong> Instant transfer</span>
            </li>
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span><strong>Ethereum:</strong> Instant transfer</span>
            </li>
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span><strong>Wise:</strong> Instant transfer</span>
            </li>
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span><strong>Remitly:</strong> Instant transfer</span>
            </li>
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span><strong>E-transfer:</strong> Instant transfer</span>
            </li>
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span><strong>Solana:</strong> Instant transfer</span>
            </li>
            <li class="flex items-center gap-2">
              <div class="w-2 h-2 bg-brand-500 rounded-full"></div>
              <span><strong>CashApp:</strong> Instant transfer</span>
            </li>
          </ul>
        </div>
        <div class="flex items-center gap-2 text-xs text-brand-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Add or update payment methods in Settings → Payment Info</span>
        </div>
      </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
];

const startAffiliatesPayoutTour = (
  tourDriver: Driver,
  onNextClick: DriverHook,
  onPrevClick: DriverHook
) => {
  tourDriver.setConfig(defaultTourConfig);
  // Here we set the unique steps for the FIRST tour
  tourDriver.setSteps(
    affiliatesPayoutsTourSteps.map((step) => ({
      ...step,
      popover: {
        ...step.popover,
        onNextClick: (element, step, config) => {
          onNextClick(element, step, config);
          setTimeout(() => {
            tourDriver.moveNext();
          }, 200);
        },
        onPrevClick: (element, step, config) => {
          onPrevClick(element, step, config);
          setTimeout(() => {
            tourDriver.movePrevious();
          }, 200);
        },
      },
    }))
  );
  tourDriver.drive();
};

export default startAffiliatesPayoutTour;
